/**
 * Preview dev de "Recursos de tu casa". Solo accesible en DEV (gate
 * import.meta.env.DEV en App.tsx). NO va a prod.
 *
 * Sprint 22 — la data mock reproduce el escenario que originó el invariante
 * "1 llavero por persona": una casa con TRES llaveros en la bolsa, el titular
 * ya con el suyo, y dos personas más registradas. Sirve para el smoke visual:
 *
 * - el titular NO debe aparecer en los selectores (ya tiene llavero);
 * - al elegir a alguien en un llavero, debe desaparecer de los otros DOS
 *   selectores al instante, sin apretar "Asignar";
 * - el tercer llavero debe quedarse sin candidatos y mostrar el aviso;
 * - en los controles remotos, la misma persona SÍ puede repetirse (el recurso
 *   escaso es el auto), pero un auto que ya tiene control no se ofrece.
 *
 * Nombres y datos inventados: nada real de un vecino va al repositorio.
 */
import { useState } from 'react';
import { MisRecursos } from '../../components/vecino/MisRecursos';
import type {
  RecursoConSuspensionRead,
  VehiculoRead,
} from '../../lib/api/types';
import type { ResidenteConPersonaRead } from '../../lib/api/residentes';

function residente(
  id: string,
  nombre: string,
  parentesco: string,
): ResidenteConPersonaRead {
  return {
    id,
    lote_id: 'lote-1',
    persona_id: `p-${id}`,
    persona_nombre_completo: nombre,
    persona_rut: null,
    persona_email: null,
    parentesco,
    estado: 'aprobado',
    desde: '2020-01-01',
    hasta: null,
    motivo_pendiente: null,
    motivo_rechazo: null,
    notas: null,
  } as unknown as ResidenteConPersonaRead;
}

const RESIDENTES: ResidenteConPersonaRead[] = [
  residente('res-1', 'Ana Ejemplo Prueba', 'titular'),
  residente('res-2', 'Bruno Ejemplo Prueba', 'conyuge'),
  residente('res-3', 'Clara Ejemplo Prueba', 'hijo'),
];

const VEHICULOS: VehiculoRead[] = [
  {
    id: 'veh-1',
    lote_id: 'lote-1',
    patente: 'AAAA-11',
    marca: 'Marca',
    modelo: 'Modelo',
    estado: 'aprobado',
    activo: true,
  } as unknown as VehiculoRead,
  {
    id: 'veh-2',
    lote_id: 'lote-1',
    patente: 'BBBB-22',
    marca: 'Marca',
    modelo: 'Modelo',
    estado: 'aprobado',
    activo: true,
  } as unknown as VehiculoRead,
];

function rec(o: Partial<RecursoConSuspensionRead>): RecursoConSuspensionRead {
  return {
    id: o.id ?? 'r',
    lote_id: 'lote-1',
    residente_actual_id: o.residente_actual_id ?? null,
    vehiculo_id: o.vehiculo_id ?? null,
    tipo: o.tipo ?? 'llavero',
    identificador: o.identificador ?? 'LL-0001',
    costo_reposicion: 5000,
    entregado_at: '2024-03-15T12:00:00Z',
    estado: o.estado ?? 'entregado',
    notas: null,
    serial_rfid: o.serial_rfid ?? null,
  };
}

const RECURSOS: RecursoConSuspensionRead[] = [
  // --- Asignados ---
  // La titular ya tiene su llavero: NO debe aparecer en los selectores.
  rec({
    id: '1',
    tipo: 'llavero',
    identificador: 'LL-0300',
    serial_rfid: '0000100300',
    residente_actual_id: 'res-1',
  }),
  // El auto AAAA-11 ya tiene control: NO debe ofrecerse ese auto.
  rec({
    id: '2',
    tipo: 'control_remoto',
    identificador: 'CR-6975-01',
    residente_actual_id: 'res-1',
    vehiculo_id: 'veh-1',
  }),
  // --- En la bolsa (sin asignar) ---
  // Tres llaveros y solo dos personas sin llavero: el tercero se queda sin
  // candidatos y debe mostrar el aviso.
  rec({
    id: '3',
    tipo: 'llavero',
    identificador: 'LL-0246',
    serial_rfid: '0000100246',
  }),
  rec({
    id: '4',
    tipo: 'llavero',
    identificador: 'LL-0247',
    serial_rfid: '0000100247',
  }),
  rec({
    id: '5',
    tipo: 'llavero',
    identificador: 'LL-0249',
    serial_rfid: '0000100249',
  }),
  // Dos controles en bolsa y un solo auto libre (BBBB-22): el segundo control
  // se queda sin auto disponible.
  rec({ id: '6', tipo: 'control_remoto', identificador: 'CR-6975-02' }),
  rec({ id: '7', tipo: 'control_remoto', identificador: 'CR-6975-03' }),
];

export function MisRecursosPreview() {
  const [log, setLog] = useState<string>('');
  return (
    <div className="min-h-screen bg-bg-page p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-xs text-text-muted mb-4">
          DEV · preview rediseño Recursos. {log}
        </div>
        <MisRecursos
          recursos={RECURSOS}
          residentes={RESIDENTES}
          vehiculos={VEHICULOS}
          onAsignado={() => setLog('(asignar llamado)')}
          onReportar={(r) => setLog(`(reportar ${r.identificador})`)}
          onSolicitar={() => setLog('(solicitar recurso)')}
        />
      </div>
    </div>
  );
}
